import React, { useCallback, useEffect, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { TextField } from "@mui/material";
import { Add, ArrowBack, CloseRounded } from "@mui/icons-material";
import { useBreakpoint, useSelectedOrganization, useSnackbar } from "../../hooks";
import { processesApi, tagsApi } from "../../api";
import { alpha, Box } from "@mui/system";
import { SxProps, Theme } from "@mui/material/styles";
import FlowentiButton from "../flowenti-ui-kit/FlowentiButton";
import { LabelData, LabelDataWithOptionalId } from "./label_Interface";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import M3Tooltip from "../common/M3Tooltip";
import LabelRow from "./LabelRow";
import LabelForm from "./LabelForm";

interface LabelManagerProps {
  idprocess?: number | undefined;
  open: boolean;
  handleClose: () => void;
  checked?: LabelData[];
  setChecked?: React.Dispatch<React.SetStateAction<LabelData[]>>;
}

const LabelManager: React.FC<LabelManagerProps> = ({
  idprocess,
  open,
  handleClose,
  checked,
  setChecked,
}) => {
  const { sendSnack } = useSnackbar();
  const selectedOrganization = useSelectedOrganization();
  const organizationId = selectedOrganization?.id;
  const isMobile = useBreakpoint("down", "md");
  const theme = useTheme();
  const [labelList, setLabelList] = useState<LabelData[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [defaultCheck, setDefaultCheck] = useState<number[]>([]);
  const [checking, setChecking] = useState<number[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<number | "new" | null>(
    null
  );
  const [labelForm, setLabelForm] = useState<LabelDataWithOptionalId | null>(
    null
  );
  const fetchLabels = useCallback(async () => {
    try {
      const promises = [tagsApi.get(`/${organizationId}/tags`)];
      if (idprocess) {
        promises.push(processesApi.get(`/${idprocess}/tags`));
      }
      const [response, fromProcess] = await Promise.all(promises);
      if (fromProcess) {
        setDefaultCheck(
          fromProcess.data.processtags.map(
            (tag: { id: number; name: string; color: string }) => tag.id
          )
        );
      }

      // Accessing 'tag' property of response data
      const labels = response.data.tag.map(
        (tag: { id: number; name: string; color: string }) => ({
          id: tag.id,
          name: tag.name,
          css: tag.color,
        })
      );
      setSelectedLabel(null);
      setLabelList(labels);
    } catch (err) {
      console.error(`Failed to fetch labels: ${err}`);
    } finally {
    }
  }, [idprocess, organizationId]);

  const translations = useTranslations();

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const handleCheckLabel = async (label: LabelData, newCheck: boolean) => {
    try {
      setChecking((_) => [..._, label.id]);
      if (newCheck) {
        if (idprocess) {
          await tagsApi.post(
            `/${organizationId}/processes/${idprocess}/tags/${label.id}`
          );
        } else {
          setChecked && setChecked((_) => [..._, label]);
        }
      } else {
        if (idprocess) {
          await tagsApi.delete(
            `/${organizationId}/processes/${idprocess}/tags/${label.id}`
          );
        } else {
          setChecked &&
            setChecked((_) => {
              const c_ = [..._];
              const c_idx = c_.findIndex((__) => __.id === label.id);
              c_.splice(c_idx, 1);
              return c_;
            });
        }
      }
    } catch {
    } finally {
      setChecking((_) => {
        const c_ = [..._];
        const c_idx = c_.findIndex((__) => __ === label.id);
        c_.splice(c_idx, 1);
        return c_;
      });
    }
  };

  const deleteLabel = async (labelId: number) => {
    try {
      await tagsApi.delete(`/${organizationId}/tags/${labelId}`);
      fetchLabels();
    } catch (err) {
      console.error(`Failed to delete label: ${err}`);
    }
  };
  const addLabel = async () => {
    if (labelForm) {
      try {
        const tagData = {
          name: labelForm.name,
          color: labelForm.css,
        };

        const { data } = await tagsApi.post(
          `/${organizationId}/tags`,
          tagData
        );

        fetchLabels();
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      } catch {
      } finally {
      }
    } else {
    }
  };
  const editLabel = async () => {
    if (labelForm) {
      try {
        const tagData = {
          name: labelForm.name,
          color: labelForm.css,
        };

        const { data } = await tagsApi.put(
          `/${organizationId}/tags/${labelForm.id}`,
          tagData
        );

        fetchLabels();
        sendSnack({
          message: data.responsemessage,
          type: "success",
        });
      } catch {}
    } else {
    }
  };

  // M3 paper chrome: 12 px corner radius (M3 medium surface), wide dialog (sm breakpoint).
  // We close over `theme` to get palette.mode without forcing the sx callback form.
  const dialogPaperSx = (theme: Theme) =>
    ({
      borderRadius: isMobile ? 0 : 4,
      bgcolor: "background.paper",
      color: "text.primary",
      boxShadow: "0 8px 24px rgba(0,0,0,0.20), 0 2px 6px rgba(0,0,0,0.12)",
      backgroundImage: "none",
      overflow: "hidden",
      ...(theme.palette.mode === "dark" && {
        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
      }),
    } as const);

  const backdropSx = (theme: Theme) =>
    ({
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      bgcolor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.black, 0.6)
          : alpha(theme.palette.common.black, 0.4),
    } as const);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: dialogPaperSx as unknown as SxProps<Theme> }}
      BackdropProps={{ sx: backdropSx as unknown as SxProps<Theme> }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <IconButton
          tabIndex={selectedLabel ? 0 : -1}
          sx={{
            opacity: selectedLabel ? 1 : 0,
            cursor: selectedLabel ? "pointer" : "default",
            pointerEvents: selectedLabel ? "auto" : "none",
          }}
          size="small"
          onClick={() => setSelectedLabel(null)}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography
          variant="h6"
          sx={{ fontFamily: "Poppins-Bold", fontSize: 20, flex: 1, textAlign: "center" }}
        >
          {getTranslation(translations, "generic.labels.caption")}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {selectedLabel ? (
          <LabelForm
            onChange={(label) => setLabelForm(label)}
            defaultLabel={
              selectedLabel === "new"
                ? undefined
                : labelList.find((_) => _.id === selectedLabel)
            }
          />
        ) : (
          <Stack direction="column" spacing={2}>
            <TextField
              value={searchValue}
              onKeyDown={e => e.stopPropagation()}
              onChange={({ target }) => setSearchValue(target.value)}
              label={getTranslation(
                translations,
                "generic.searchlabels.caption"
              )}
              variant="outlined"
              size="medium"
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  bgcolor: "background.paper",
                  paddingRight: "6px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <M3Tooltip
                      title={getTranslation(translations, "generic.createnewlabel.caption")}
                    >
                      <IconButton
                        onClick={() => setSelectedLabel("new")}
                        size="small"
                        sx={{
                          color: theme.palette.primary.main,
                        }}
                        aria-label={getTranslation(
                          translations,
                          "generic.createnewlabel.caption"
                        )}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </M3Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                maxHeight: 360,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {Array.isArray(labelList) &&
                labelList
                  .filter((x) =>
                    x.name.toLowerCase().includes(searchValue.toLowerCase())
                  )
                  .map((label: LabelData) => (
                    <Stack
                      key={label.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        minHeight: 48,
                        px: 1,
                        borderRadius: 2,
                        "&:hover": {
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.04)"
                              : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      {idprocess ? (
                        <Checkbox
                          defaultChecked={defaultCheck.includes(label.id)}
                          disabled={checking.includes(label.id)}
                          onChange={(_e, newCheck) =>
                            handleCheckLabel(label, newCheck)
                          }
                          sx={{ color: (_) => _.palette.grey[600] }}
                        />
                      ) : (
                        <Checkbox
                          checked={
                            !!checked?.find((_) => _.id === label.id)
                          }
                          onChange={(_e, newCheck) =>
                            handleCheckLabel(label, newCheck)
                          }
                          sx={{ color: (_) => _.palette.grey[600] }}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <LabelRow
                          color={label.css}
                          name={label.name}
                          onEditClick={() => setSelectedLabel(label.id)}
                        />
                      </Box>
                    </Stack>
                  ))}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        {selectedLabel ? (
          <Stack
            flex={1}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <FlowentiButton
              variant="outlined"
              size="small"
              component="label"
              onClick={selectedLabel === "new" ? addLabel : editLabel}
            >
              {getTranslation(
                translations,
                String(selectedLabel) !== "new"
                  ? "generic.button.savechanges"
                  : "generic.create.caption"
              )}
            </FlowentiButton>
            {selectedLabel !== "new" && (
              <FlowentiButton
                variant="outlined"
                size="small"
                color="error"
                component="label"
                onClick={() => deleteLabel(selectedLabel)}
              >
                {getTranslation(translations, "generic.delete.caption")}
              </FlowentiButton>
            )}
          </Stack>
        ) : (
          // Confirm action: just closes the dialog. Reuses the same translation
          // key as the field dialog (pm.mayactivities.confirmation.label) and the
          // FlowentiButton used throughout the app footer.
          <FlowentiButton
            variant="contained"
            size="small"
            color="primary"
            onClick={handleClose}
            sx={{ minWidth: 120 }}
          >
            {getTranslation(
              translations,
              "pm.mayactivities.confirmation.label"
            )}
          </FlowentiButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LabelManager;
